// Root layout (B5B §3.1): SessionProvider → I18nProvider → QueryClientProvider → the shadcn persona
// AppShell. PLAN-C1.9 + the FRONTEND-BLUEPRINT persona rebuild. The flat link nav was replaced by the
// six-persona AppShell; the impersonation + mandate banners ride above it.
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { SessionProvider, useSession } from "../lib/session";
import { handleSessionExpired } from "../lib/auth";
import { I18nProvider, useI18n } from "../lib/i18n";
import { AUTH_BASE } from "../lib/env";
import { AppShell } from "../components/app-shell";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

// F5 — persistent banner whenever the session is a super-tenant operator acting as a tenant.
function ImpersonationBanner() {
  const session = useSession();
  const { t } = useI18n();
  if (!session.impersonatorUserId) return null;
  const end = async () => {
    await fetch(`${AUTH_BASE}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    window.location.href = `${AUTH_BASE}/auth/login`;
  };
  return (
    <div role="alert" className="flex items-center gap-3 bg-amber-900 px-5 py-2 text-sm text-white">
      <span>⚠️ {t("imp.banner", session.activeTenantId)}{session.impersonatorEmail ? ` · ${session.impersonatorEmail}` : ""}</span>
      <button type="button" onClick={end} className="ml-auto rounded-md bg-white px-2.5 py-0.5 text-xs font-semibold text-amber-900">{t("imp.end")}</button>
    </div>
  );
}

// P1 / B3-RBAC-CATALOG §2.5 — banner when this session is a partner consultant acting under a mandate.
function MandateBanner() {
  const session = useSession();
  const { t } = useI18n();
  if (!session.actingAsPartnerUserId || !session.mandateScope) return null;
  const scope = session.mandateScope;
  const parts = [
    scope.actions?.length ? `${t("mandate.actions")}: ${scope.actions.join(", ")}` : null,
    scope.modules?.length ? `${t("mandate.modules")}: ${scope.modules.join(", ")}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <div role="status" className="flex items-center gap-3 bg-blue-900 px-5 py-2 text-sm text-white">
      <span>🔐 {t("mandate.banner", session.activeTenantId)}{parts ? ` · ${parts}` : ` · ${t("mandate.full")}`}</span>
    </div>
  );
}

// A 401 that survives the api-client's silent refresh-and-retry means the session is genuinely over →
// send the user through AuthKit to re-establish it. Don't retry 4xx (auth/client errors won't self-heal);
// retry transient network/5xx once.
const onAuthError = (err: unknown) => { if (err instanceof ApiError && err.status === 401) handleSessionExpired(); };
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && failureCount < 1,
    },
  },
});

export function RootLayout() {
  return (
    <SessionProvider>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={300}>
            <ImpersonationBanner />
            <MandateBanner />
            <AppShell />
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </I18nProvider>
    </SessionProvider>
  );
}
